import { a11yHideSlide, a11yShowSlide } from '../../../../design3/asset-build/js/custom/carousel';
import clone from 'lodash/clone';
import debounce from 'lodash/debounce';
import map from 'lodash/map';
import { getBreakpoint, getRTL } from '../../../../design3/asset-build/js/custom/_utils';
import '../../../../design3/asset-build/js/custom/scrollTrigger';
import { Draggable } from 'gsap/Draggable';
import { gsap } from 'gsap';
import { triggerEvent } from '../analytics/analytics';

const baseClass = 'GlobalFeaturedProperties';
const carouselClass = 'AnimatedCardCarouselBackground';
const carouselPrimitiveClass = 'CenterFocusCarousel';
const activeSectionClass = `${baseClass}-Body-section--active`;
const activeButtonClass = `${baseClass}-AnimatedTitle-button--active`;

const selectors = {
	base: `.${baseClass}`,
	body: `.${baseClass}-Body`,
	bodySection: `.${baseClass}-Body-section`,
	activeBodySection: `.${activeSectionClass}`,
	carousel: `.${baseClass}-Body-Carousel`,
	ctaBlock: `.${baseClass}-Body-CTABlock`,
	card: `.${carouselClass}-Card`,
	slide: `.${carouselClass}--slide`,
	carouselBackground: `.${carouselClass}`,
	backgroundImage: `.${carouselClass}-BackgroundImage`,
	desktopCarousel: `.${carouselPrimitiveClass}-container--desktop`,
	mobileCarousel: `.${carouselPrimitiveClass}-container--mobile`,
	animatedTitle: `.${baseClass}-AnimatedTitle`,
	animatedTitleUnderline: `.${baseClass}-AnimatedTitle--underline`,
	mobileTitleContainer: `.${baseClass}-AnimatedTitle--mobile`,
	flickityDesktop: `.${carouselPrimitiveClass}-container--desktop .${carouselPrimitiveClass}--list`,
	flickityMobile: `.${carouselPrimitiveClass}-container--mobile .${carouselPrimitiveClass}--list`,
};

const bulletWidth = 64;
const activeScale = 1.3333333333;
const activeScaleMobile = 1.15;

const replaceCards = ($container, id) => {
	if (window.FS && window.FS[id]) {
		const htmlSlides = window.FS[id]();

		let selectedSlides = [];
		for (let i = 0; i < 5; i++) {
			let randomIndex = Math.floor(Math.random() * htmlSlides.length);
			while (selectedSlides.includes(htmlSlides[randomIndex])) {
				randomIndex = Math.floor(Math.random() * htmlSlides.length);
			}
			selectedSlides.push(htmlSlides[randomIndex]);
		}

		const $carousel = $container.find(`#${id}`);

		const $randomDesktopSlides = $(
			$.parseHTML(map(selectedSlides, 'desktop').join(''))[0].textContent
		);
		const $randomMobileSlides = $(
			$.parseHTML(map(selectedSlides, 'mobile').join(''))[0].textContent
		);
		const $randomBackgrounds = $(
			$.parseHTML(map(selectedSlides, 'background').join(''))[0].textContent
		);

		//Remove the old and set the new background images
		const $carouselBackground = $carousel.find(selectors.carouselBackground);
		$carouselBackground.find(selectors.backgroundImage).remove();
		$carouselBackground.find('style').remove();
		$randomBackgrounds.first().addClass(`${carouselClass}-BackgroundImage--active`);
		$carouselBackground.prepend($randomBackgrounds);

		//slides are multiplied for layout, so we need to replace all of them, iterate through the current slides and pluck the correct one from selected slides
		let randomCardIndex = 0;
		$carousel
			.find(selectors.desktopCarousel)
			.find(selectors.slide)
			.each((index, slide) => {
				//always 5 cards so loop back every 5
				let $slide = $(slide);
				const $currentRandomSlide = $($randomDesktopSlides.get(randomCardIndex));
				$slide.data('bgImageId', $currentRandomSlide.data('bgImageId'));
				$slide.attr('bg-image-id', $currentRandomSlide.data('bgImageId'));
				$slide.html($currentRandomSlide.get(0).innerHTML);
				randomCardIndex = randomCardIndex === 4 ? 0 : randomCardIndex + 1;
			});
		$carousel
			.find(selectors.mobileCarousel)
			.find(selectors.slide)
			.each((index, slide) => {
				let $slide = $(slide);
				const $currentRandomSlide = $($randomMobileSlides.get(index));
				$slide.html($currentRandomSlide.get(0).innerHTML);
			});
	}
};

const initRandomCards = $container => {
	const id = $container.attr('id');

	//Residences
	const resId = `${id}_residences`;
	replaceCards($container, resId);

	//Private Retreats
	const privateId = `${id}_private`;
	replaceCards($container, privateId);
};

const initAnimatedTitle = ($container, initialActiveId) => {
	let tallestHeights = fixHeightsAndPositions($container);

	const id = $container.attr('id');

	const $animatedTitle = $container.find(selectors.animatedTitle);
	const $animatedTitleUnderline = $container.find(selectors.animatedTitleUnderline);

	const $residencesButton = $container.find(`button${selectors.animatedTitle}--residences`);
	const $hotelsButton = $container.find(`button${selectors.animatedTitle}--hotels`);
	const $privateButton = $container.find(`button${selectors.animatedTitle}--private`);

	$animatedTitle.css('height', $hotelsButton.height() * activeScale);

	const residencesButtonActiveWidth = $residencesButton.width() * activeScale;
	const residencesButtonInactiveWidth = $residencesButton.width();

	const hotelsButtonActiveWidth = $hotelsButton.width() * activeScale;
	const hotelsButtonInactiveWidth = $hotelsButton.width();

	const privateButtonActiveWidth = $privateButton.width() * activeScale;
	const privateButtonInactiveWidth = $privateButton.width();

	const letterSpacing = Number.parseFloat($hotelsButton.css('letter-spacing').replace('px', ''));

	const fullTitleWidth = {
		residences:
			residencesButtonActiveWidth +
			bulletWidth +
			hotelsButtonInactiveWidth +
			bulletWidth +
			privateButtonInactiveWidth -
			letterSpacing,
		hotels:
			residencesButtonInactiveWidth +
			bulletWidth +
			hotelsButtonActiveWidth +
			bulletWidth +
			privateButtonInactiveWidth -
			letterSpacing,
		private:
			residencesButtonInactiveWidth +
			bulletWidth +
			hotelsButtonInactiveWidth +
			bulletWidth +
			privateButtonActiveWidth -
			letterSpacing,
	};

	const setActiveTitle = activeId => {
		if (activeId === `${id}_residences_desktop`) {
			$residencesButton.addClass(activeButtonClass).css('width', residencesButtonActiveWidth);
			$privateButton.removeClass(activeButtonClass).css('width', privateButtonInactiveWidth);
			$hotelsButton.removeClass(activeButtonClass).css('width', hotelsButtonInactiveWidth);
			$container.find(`#${id}_residences`).addClass(activeSectionClass).attr('aria-hidden', false);
			$animatedTitleUnderline.css('width', residencesButtonActiveWidth - letterSpacing);
			const translateX =
				fullTitleWidth.residences / 2 - residencesButtonActiveWidth / 2 + letterSpacing;
			$animatedTitle.css('transform', `translateX(${getRTL() ? -1 * translateX : translateX}px)`);
		} else if (activeId === `${id}_hotels_desktop`) {
			$hotelsButton.addClass(activeButtonClass).css('width', hotelsButtonActiveWidth);
			$residencesButton.removeClass(activeButtonClass).css('width', residencesButtonInactiveWidth);
			$privateButton.removeClass(activeButtonClass).css('width', privateButtonInactiveWidth);
			$container.find(`#${id}_hotels`).addClass(activeSectionClass).attr('aria-hidden', false);
			$animatedTitleUnderline.css('width', hotelsButtonActiveWidth - letterSpacing);
			const translateX =
				fullTitleWidth.hotels / 2 -
				(residencesButtonInactiveWidth + bulletWidth + hotelsButtonActiveWidth / 2) +
				letterSpacing;
			$animatedTitle.css('transform', `translateX(${getRTL() ? -1 * translateX : translateX}px)`);
		} else if (activeId === `${id}_private_desktop`) {
			$privateButton.addClass(activeButtonClass).css('width', privateButtonActiveWidth);
			$residencesButton.removeClass(activeButtonClass).css('width', residencesButtonInactiveWidth);
			$hotelsButton.removeClass(activeButtonClass).css('width', hotelsButtonInactiveWidth);
			$animatedTitleUnderline.css('width', privateButtonActiveWidth - letterSpacing);
			const translateX =
				fullTitleWidth.private / 2 -
				(residencesButtonInactiveWidth +
					bulletWidth +
					hotelsButtonInactiveWidth +
					bulletWidth +
					privateButtonActiveWidth / 2) +
				letterSpacing;
			$animatedTitle.css('transform', `translateX(${getRTL() ? -1 * translateX : translateX}px)`);
		}

		const $bodySections = $container.find(selectors.bodySection);
		$bodySections.removeClass(activeSectionClass).attr('aria-hidden', true);
		$bodySections.find(selectors.slide).each((index, slide) => {
			a11yHideSlide(slide);
		});
		$bodySections
			.find(
				'.GlobalFeaturedProperties-Body-CTABlock a.CTA, .CenterFocusCarousel-pagination--button'
			)
			.attr('tabindex', -1);
		const $newActiveSection = $container.find(`#${activeId.replace('_desktop', '')}`);
		$newActiveSection.addClass(activeSectionClass).attr('aria-hidden', false);
		$newActiveSection
			.find(
				'.GlobalFeaturedProperties-Body-CTABlock a.CTA, .CenterFocusCarousel-pagination--button'
			)
			.attr('tabindex', 0);
		a11yShowSlide($newActiveSection.find(`${selectors.slide}.is-selected`)[0]);
	};

	setActiveTitle(initialActiveId || $hotelsButton.attr('id'));
	const isHomePage = window.FS && window.FS.globalInfo && window.FS.globalInfo.isHomepage;

	//Listeners
	$residencesButton.on('click', e => {
		if (isHomePage) {
			triggerEvent('home', 'featured properties', 'Residences');
		}
		setActiveTitle($residencesButton.attr('id'));
	});

	$hotelsButton.on('click', e => {
		if (isHomePage) {
			triggerEvent('home', 'featured properties', 'Hotels & Resorts');
		}
		setActiveTitle($hotelsButton.attr('id'));
	});

	$privateButton.on('click', e => {
		if (isHomePage) {
			triggerEvent('home', 'featured properties', 'Private Retreats');
		}
		setActiveTitle($privateButton.attr('id'));
	});

	$(window).on(
		'resize',
		debounce(() => {
			setActiveTitle(`${$container.find(selectors.activeBodySection).attr('id')}_desktop`);
			if (getBreakpoint() !== 'small') {
				fixHeightsAndPositions($container, tallestHeights);
			}
		})
	);
};

const initAnimatedTitleMobile = ($container, initialActiveId) => {
	let tallestHeights = fixHeightsAndPositions($container);

	const id = $container.attr('id');
	let screenWidth = $(window).innerWidth();
	const $titleContainer = $container.find(selectors.mobileTitleContainer);
	const $hotels = $titleContainer.find(
		'.GlobalFeaturedProperties-AnimatedTitle--title--mobile--hotels'
	);
	const $residences = $titleContainer.find(
		'.GlobalFeaturedProperties-AnimatedTitle--title--mobile--residences'
	);
	const $private = $titleContainer.find(
		'.GlobalFeaturedProperties-AnimatedTitle--title--mobile--private'
	);

	const $underline = $titleContainer.find(
		'.GlobalFeaturedProperties-AnimatedTitle--mobile--underline'
	);

	const hotelsActiveWidth = $hotels.width() * activeScaleMobile;
	const hotelsInactiveWidth = $hotels.width();

	const residencesActiveWidth = $residences.width() * activeScaleMobile;
	const residencesInactiveWidth = $residences.width();

	const privateActiveWidth = $private.width() * activeScaleMobile;
	const privateInactiveWidth = $private.width();

	const letterSpacing = Number.parseFloat($hotels.css('letter-spacing').replace('px', ''));

	const fullTitleWidth = {
		residences: residencesActiveWidth + hotelsInactiveWidth + privateInactiveWidth + 60 + 54,
		hotels: residencesInactiveWidth + hotelsActiveWidth + privateInactiveWidth + 60 + 54,
		private: residencesInactiveWidth + hotelsInactiveWidth + privateActiveWidth + 60 + 54,
	};

	let hotelsPosition = {
		element: $hotels,
		position: getRTL()
			? screenWidth / 2 - fullTitleWidth.hotels / 2
			: fullTitleWidth.hotels / 2 - screenWidth / 2,
	};
	let privatePosition = {
		element: $private,
		position: getRTL()
			? fullTitleWidth.private / 2 - screenWidth / 2
			: screenWidth / 2 - fullTitleWidth.private / 2,
	};
	let residencesPosition = {
		element: $residences,
		position: getRTL()
			? hotelsInactiveWidth +
			  15 +
			  (residencesActiveWidth + 30) / 2 -
			  fullTitleWidth.residences / 2 +
			  (26.5 - letterSpacing)
			: fullTitleWidth.residences / 2 -
			  (hotelsInactiveWidth + 15 + (residencesActiveWidth + 30) / 2) -
			  (26.5 - letterSpacing),
	};

	const positions = [hotelsPosition, residencesPosition, privatePosition];

	const getClosestPosition = x => {
		let closest = hotelsPosition;
		positions.forEach(current => {
			closest = Math.abs(current.position - x) < Math.abs(closest.position - x) ? current : closest;
		});
		return closest;
	};

	const active = 'GlobalFeaturedProperties-AnimatedTitle--title--mobile--active';

	const setActiveTitle = activeId => {
		if (activeId === `${id}_hotels_mobile`) {
			$draggableContainer.addClass('GlobalFeaturedProperties-AnimatedTitle--mobile--animated');
			gsap.set($draggableContainer, { x: hotelsPosition.position });
			$hotels.addClass(active).css('width', hotelsActiveWidth);
			const translateX =
				screenWidth / 2 - hotelsActiveWidth - 27 + hotelsActiveWidth / 2 + letterSpacing;
			$underline
				.css('width', hotelsActiveWidth)
				.css('transform', `translateX(${getRTL() ? translateX : -1 * translateX}px)`);
			$residences.removeClass(active).css('width', residencesInactiveWidth);
			$private.removeClass(active).css('width', privateInactiveWidth);
		} else if (activeId === `${id}_residences_mobile`) {
			$draggableContainer.addClass('GlobalFeaturedProperties-AnimatedTitle--mobile--animated');
			gsap.set($draggableContainer, { x: residencesPosition.position });
			$residences.addClass(active).css('width', residencesActiveWidth);
			$underline
				.css('width', residencesActiveWidth)
				.css(
					'transform',
					`translateX(${getRTL() ? (-1 * letterSpacing) / 2 : letterSpacing / 2}px)`
				);
			$hotels.removeClass(active).css('width', hotelsInactiveWidth);
			$private.removeClass(active).css('width', privateInactiveWidth);
		} else if (activeId === `${id}_private_mobile`) {
			$draggableContainer.addClass('GlobalFeaturedProperties-AnimatedTitle--mobile--animated');
			gsap.set($draggableContainer, { x: privatePosition.position });
			$private.addClass(active).css('width', privateActiveWidth);
			const translateX =
				screenWidth / 2 - privateActiveWidth - 27 + privateActiveWidth / 2 - letterSpacing;
			$underline
				.css('width', privateActiveWidth)
				.css('transform', `translateX(${getRTL() ? -1 * translateX : translateX}px)`);
			$hotels.removeClass(active).css('width', hotelsInactiveWidth);
			$residences.removeClass(active).css('width', residencesInactiveWidth);
		}
		$container
			.find(selectors.activeBodySection)
			.removeClass(activeSectionClass)
			.attr('aria-hidden', true);
		$container
			.find(`#${activeId.replace('_mobile', '')}`)
			.addClass(activeSectionClass)
			.attr('aria-hidden', false);
	};

	const $draggableContainer = $container.find(
		'.GlobalFeaturedProperties-AnimatedTitle--mobile--drag'
	);
	let draggable = Draggable.create($draggableContainer, {
		type: 'x',
		bounds: {
			minX: hotelsPosition.position,
			maxX: privatePosition.position,
		},
		onDragStart: function () {
			$draggableContainer.removeClass('GlobalFeaturedProperties-AnimatedTitle--mobile--animated');
		},
		onDragEnd: function () {
			let moveTo = getClosestPosition(this.x);
			$draggableContainer.addClass('GlobalFeaturedProperties-AnimatedTitle--mobile--animated');
			setActiveTitle(moveTo.element.attr('id'));
		},
	});

	setActiveTitle(initialActiveId || $hotels.attr('id'));

	$hotels.on('click', e => {
		setActiveTitle($hotels.attr('id'));
	});

	$residences.on('click', e => {
		setActiveTitle($residences.attr('id'));
	});

	$private.on('click', e => {
		setActiveTitle($private.attr('id'));
	});

	$(window).on(
		'resize',
		debounce(() => {
			screenWidth = $(window).innerWidth();
			hotelsPosition.position = getRTL()
				? screenWidth / 2 - fullTitleWidth.hotels / 2
				: fullTitleWidth.hotels / 2 - screenWidth / 2;
			privatePosition.position = getRTL()
				? fullTitleWidth.private / 2 - screenWidth / 2
				: screenWidth / 2 - fullTitleWidth.private / 2;
			draggable.bounds = {
				minX: hotelsPosition.position,
				maxX: privatePosition.position,
			};

			const activeId = $draggableContainer.find(`.${active}`).attr('id');
			setActiveTitle(activeId);
			if (getBreakpoint() === 'small') {
				fixHeightsAndPositions($container, tallestHeights);
			}
		})
	);
};

const fixHeightsAndPositions = ($container, current) => {
	const $body = $container.find(selectors.body);
	const $bodySections = $container.find(selectors.bodySection);

	$body.css('height', '');
	$bodySections.find(selectors.carousel).css('top', '');
	$bodySections.find(selectors.ctaBlock).css('height', '');

	let tallestHeights = current
		? clone(current)
		: {
				carousel: 0,
				ctaBlock: 0,
				bodySection: 0,
		  };
	$bodySections.each((index, section) => {
		const $section = $(section);
		const ctaBlockHeight = $section.find(selectors.ctaBlock).outerHeight();
		const carouselHeight = $section.find(selectors.carousel).outerHeight();
		const bodySectionHeight = $section.outerHeight();

		tallestHeights.carousel = Math.max(tallestHeights.carousel, carouselHeight);
		tallestHeights.ctaBlock = Math.max(tallestHeights.ctaBlock, ctaBlockHeight);
		tallestHeights.bodySection = Math.max(tallestHeights.bodySection, bodySectionHeight);
	});
	if (getBreakpoint() !== 'small') {
		$body.css('height', tallestHeights.carousel + tallestHeights.ctaBlock - 100);
		$bodySections.find(selectors.carousel).css('top', tallestHeights.ctaBlock - 100);
	} else {
		$body.css('height', tallestHeights.bodySection);
	}
	$bodySections.find(selectors.ctaBlock).css('height', tallestHeights.ctaBlock);

	return tallestHeights;
};

const setupAnimationTrigger = $container => {
	const id = $container.attr('id');
	const $backgroundImage = $container.find(`#${id}_hotels ${selectors.backgroundImage}--active`);
	const $carousel = $container.find(`#${id}_hotels ${selectors.desktopCarousel}`);
	$backgroundImage.css('transform', 'scale(1.1, 1.1)');
	$carousel.css('transform', `translateX(${getRTL() ? '' : '-'}60px)`);

	gsap.to($carousel, {
		scrollTrigger: {
			trigger: $container.get(0),
			start: `top+=45% bottom`,
		},
		translateX: 0,
		duration: 1,
	});
	gsap.to($backgroundImage, {
		scrollTrigger: {
			trigger: $container.get(0),
			start: `top+=45% bottom`,
		},
		scale: 1,
		duration: 1,
	});
};

export default function initGlobalFeaturedProperties() {
	const setupMobile = $container => {
		initAnimatedTitleMobile($container);
		const initAfterResize = debounce(function () {
			if (getBreakpoint() !== 'small') {
				initAnimatedTitle(
					$container,
					`${$container.find(selectors.activeBodySection).attr('id')}_desktop`
				);
				setupAnimationTrigger($container);
				$(window).off('resize', initAfterResize);
			}
		});
		$(window).on('resize', initAfterResize);
	};
	const setupDesktop = $container => {
		initAnimatedTitle($container);
		setupAnimationTrigger($container);
		const initAfterResize = debounce(function () {
			if (getBreakpoint() === 'small') {
				initAnimatedTitleMobile(
					$container,
					`${$container.find(selectors.activeBodySection).attr('id')}_mobile`
				);
				$(window).off('resize', initAfterResize);
			}
		});
		$(window).on('resize', initAfterResize);
	};
	$(selectors.base).each((i, container) => {
		const $container = $(container);
		const $flickityDesktop = $container.find(selectors.flickityDesktop);
		const $flickityMobile = $container.find(selectors.flickityMobile);

		let carouselReadyCount = 0;

		initRandomCards($container);

		if (getBreakpoint() === 'small') {
			if ($flickityMobile.hasClass('flickity-enabled')) {
				setupMobile($container);
			} else {
				$flickityMobile.on('ready.flickity', () => {
					carouselReadyCount += 1;
					if (carouselReadyCount === $flickityMobile.length) {
						setupMobile($container);
					}
				});
			}
		} else {
			if ($flickityDesktop.hasClass('flickity-enabled')) {
				setupDesktop($container);
			} else {
				$flickityDesktop.on('ready.flickity', () => {
					carouselReadyCount += 1;
					if (carouselReadyCount === $flickityDesktop.length) {
						setupDesktop($container);
					}
				});
			}
		}
	});
}
